import type { Bootstrap, TrailPoint } from "../types";

export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...options,
    credentials: "include",
    headers: {
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...options.headers,
    },
  });
  const data = (await response.json().catch(() => ({}))) as {
    error?: string;
    code?: string;
  };
  if (!response.ok) {
    throw new ApiError(
      data.error ?? "เชื่อมต่อระบบไม่สำเร็จ",
      response.status,
      data.code,
    );
  }
  return data as T;
}

export const api = {
  me: () =>
    request<{
      user: { id: string; name: string; email: string };
      membership: unknown;
    }>("/me"),
  bootstrap: () => request<Bootstrap>("/bootstrap"),
  login: (input: { email: string; password: string }) =>
    request<{ user: { id: string; name: string; email: string } }>(
      "/auth/login",
      { method: "POST", body: JSON.stringify(input) },
    ),
  register: (input: {
    name: string;
    email: string;
    password: string;
    spaceName?: string;
    inviteCode?: string;
  }) =>
    request<{ user: { id: string; name: string; email: string } }>(
      "/auth/register",
      { method: "POST", body: JSON.stringify(input) },
    ),
  logout: () => request<{ ok: true }>("/auth/logout", { method: "POST" }),
  updateLocation: (input: {
    latitude: number;
    longitude: number;
    accuracy: number;
    heading: number | null;
    speed: number | null;
    battery: number | null;
    capturedAt: string;
  }) =>
    request<{
      accepted: boolean;
      stored: boolean;
      newVisits: Array<{ id: string; name: string; emoji: string }>;
    }>("/location", { method: "POST", body: JSON.stringify(input) }),
  setSharing: (enabled: boolean) =>
    request<{ enabled: boolean }>("/sharing", {
      method: "PATCH",
      body: JSON.stringify({ enabled }),
    }),
  history: (userId: string, hours = 24) =>
    request<{ points: TrailPoint[] }>(
      `/history?userId=${encodeURIComponent(userId)}&hours=${hours}`,
    ),
  clearHistory: () =>
    request<{ ok: true }>("/history", { method: "DELETE" }),
  createPlace: (input: {
    name: string;
    category: string;
    emoji: string;
    latitude: number;
    longitude: number;
    radius: number;
  }) =>
    request<{ place: Bootstrap["places"][number] }>("/places", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  checkIn: (placeId: string) =>
    request<{ visit: unknown }>("/check-in", {
      method: "POST",
      body: JSON.stringify({ placeId }),
    }),
  sendSos: (message?: string) =>
    request<{ alert: Bootstrap["alerts"][number] }>("/alerts/sos", {
      method: "POST",
      body: JSON.stringify({ message }),
    }),
  resolveAlert: (alertId: string) =>
    request<{ id: string; resolvedAt: string }>(
      `/alerts/${encodeURIComponent(alertId)}/resolve`,
      { method: "POST" },
    ),
  regenerateInvite: () =>
    request<{ inviteCode: string }>("/invite/regenerate", {
      method: "POST",
    }),
};
